import { ChangeDetectionStrategy, Component, computed, signal, ViewEncapsulation } from '@angular/core';
import { ToastComponent } from './toast.component';
import { ToastData } from './toast.service';

export interface ToastItem {
  id: number;
  data: ToastData;
  closeFn: () => void;
}

/** Toasts beyond this many wait until one is dismissed. */
export const MAX_VISIBLE_TOASTS = 4;

const TYPE_LABEL: Record<ToastData['type'], string> = {
  error: 'Error',
  warning: 'Warning',
  success: 'Success',
  info: 'Information',
  debug: 'Information',
};

/** Plain text of a toast message, which may contain markup. */
export function toastPlainText(text: string): string {
  if (typeof DOMParser === 'undefined') return text;
  // A parsed document is inert: no scripts run and no resources load.
  const body = new DOMParser().parseFromString(text, 'text/html').body;
  return (body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * The toast stack, top centre below the header: newest on top, every visible
 * toast readable and dismissible. Two live regions that exist before
 * any toast arrives announce new ones (errors and warnings assertively).
 */
@Component({
  selector: 'app-toast-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ToastComponent],
  template: `
    <div class="toast-stack" role="region" aria-label="Notifications" [attr.aria-hidden]="visible().length ? null : 'true'">
      @for (toast of visible(); track toast.id; let i = $index) {
        <app-toast [data]="toast.data" [index]="i" (closed)="removeToast(toast.id)" />
      }
    </div>
    <div class="toast-live" aria-live="polite" aria-atomic="true">{{ politeMessage() }}</div>
    <div class="toast-live" aria-live="assertive" aria-atomic="true">{{ assertiveMessage() }}</div>
  `,
  styles: [
    `
      .toast-stack {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        width: min(22.5rem, calc(100vw - 2rem));
        pointer-events: none;
      }
      .toast-live {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
    `,
  ],
})
export class ToastContainerComponent {
  readonly toasts = signal<ToastItem[]>([]);
  /** Newest first, rendered top-down. */
  readonly visible = computed(() => this.toasts().slice(0, MAX_VISIBLE_TOASTS));
  readonly politeMessage = signal('');
  readonly assertiveMessage = signal('');

  addToast(item: ToastItem): void {
    this.toasts.update((list) => [item, ...list]);
    this.announce(item.data);
  }

  removeToast(id: number): void {
    const toast = this.toasts().find((t) => t.id === id);
    if (toast) {
      this.toasts.update((list) => list.filter((t) => t.id !== id));
      toast.closeFn();
    }
  }

  private announce(data: ToastData): void {
    const urgent = data.type === 'error' || data.type === 'warning';
    const target = urgent ? this.assertiveMessage : this.politeMessage;
    const message = `${TYPE_LABEL[data.type]}: ${toastPlainText(data.text)}`;
    // Cleared first so a repeated message is announced again.
    target.set('');
    setTimeout(() => target.set(message), 50);
  }
}
